import { useMutation } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/hooks/use-permissions';
import { productItemsService } from '@/services/product-items.service';

/**
 * Type the number on the tag, land on the piece.
 *
 * The showroom's own handle on its stock is the number written on the garment,
 * and the question behind it is nearly always the same: is this one free, who
 * has it booked, when is it back. So this is an exact lookup that *navigates*,
 * not a search that filters — the staff member already knows which piece they
 * are holding.
 *
 * The barcode is accepted on the same footing, because a scanner is a keyboard
 * that types the printed code and presses Enter.
 */
export const PieceLookup = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [code, setCode] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const lookup = useMutation({
    mutationFn: (value: string) => productItemsService.lookup(value),
    onSuccess: (item) => {
      setCode('');
      setMobileOpen(false);
      navigate(`/product-items/${item.id}`);
    },
    onError: (_error, value) => {
      // Named, not generic: "not found" without the number leaves the user
      // wondering whether the box even read what they typed.
      toast.error(`No piece numbered ${value}`);
    },
  });

  if (!can('product_items:view')) return null;

  const submit = (submitEvent: FormEvent) => {
    submitEvent.preventDefault();
    const value = code.trim();
    if (value) lookup.mutate(value);
  };

  const field = ({ autoFocus = false, onBand = false }: { autoFocus?: boolean; onBand?: boolean }) => (
    <Input
      value={code}
      onChange={(changeEvent) => setCode(changeEvent.target.value)}
      placeholder="Piece no."
      aria-label="Open a piece by its number"
      // On the header band the field is a tinted well: a white box on a solid
      // colour reads as a hole punched through it. In the dialog it is an
      // ordinary input on an ordinary sheet.
      className={
        onBand
          ? 'border-topbar-border bg-topbar-foreground/10 text-topbar-foreground placeholder:text-topbar-muted focus-visible:ring-topbar-foreground/40'
          : undefined
      }
      // The numeric keypad on a phone, because the numbers are numbers.
      inputMode="numeric"
      enterKeyHint="go"
      autoFocus={autoFocus}
      disabled={lookup.isPending}
    />
  );

  return (
    <>
      <form onSubmit={submit} className="hidden sm:block">
        <div className="w-28 lg:w-36">{field({ onBand: true })}</div>
      </form>

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Open a piece by its number"
        onClick={() => setMobileOpen(true)}
        className="text-topbar-muted hover:bg-topbar-foreground/10 hover:text-topbar-foreground sm:hidden"
      >
        <ScanLine />
      </Button>

      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Open a piece</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form onSubmit={submit} className="flex gap-2">
              {field({ autoFocus: true })}
              <Button type="submit" disabled={lookup.isPending || !code.trim()}>
                Go
              </Button>
            </form>
            <p className="mt-2 text-xs text-muted-foreground">
              Type the number written on the garment, or scan its barcode.
            </p>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};
